import React, { memo, useMemo } from "react";
import { ApiKey } from "../types/api";
import { ApiKeyRow } from "./ApiKeyRow";
import { LoadingSpinner } from "./LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertCircle, Database, Sparkles } from "lucide-react";

interface ApiKeysTableProps {
  apiKeys: ApiKey[];
  isLoading: boolean;
  error: string | null;
  onDelete: (id: string) => Promise<boolean>;
}

export const ApiKeysTable: React.FC<ApiKeysTableProps> = memo(
  ({ apiKeys, isLoading, error, onDelete }) => {
    const sortedKeys = useMemo(() => {
      return [...apiKeys].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }, [apiKeys]);

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
            <LoadingSpinner size="large" />
          </div>
          <p className="mt-6 text-sm text-slate-600 dark:text-slate-400 font-medium">
            Loading your API keys...
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
            This should only take a moment
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="relative mb-4">
            <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl"></div>
            <div className="relative bg-red-100 dark:bg-red-900/30 p-3 rounded-full">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Unable to load API keys
          </h3>
          <p className="text-red-600 dark:text-red-400 mb-6 max-w-md">
            {error}
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
          >
            Try again
          </Button>
        </div>
      );
    }

    if (sortedKeys.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-2xl"></div>
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 p-6 rounded-full border border-blue-200/50 dark:border-blue-800/50">
              <Database className="h-12 w-12 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <div className="space-y-3 max-w-md">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-500" />
              Ready to get started?
            </h3>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Create your first API key to unlock the power of the Forge AI
              assistant. Each key provides secure, authenticated access to our
              development tools.
            </p>
          </div>

          <div className="mt-6 p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              💡 <strong>Tip:</strong> Give your API key a descriptive name like
              "Production App" or "Development Environment"
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        {/* Enhanced table with better styling */}
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200/60 dark:border-slate-700/60">
              <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                Name
              </TableHead>
              <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                Secret Key
              </TableHead>
              <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                Created
              </TableHead>
              <TableHead className="w-[100px] font-semibold text-slate-700 dark:text-slate-300">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedKeys.map((key, index) => (
              <ApiKeyRow
                key={key.id}
                apiKey={key}
                onDelete={onDelete}
                isFirst={index === 0}
              />
            ))}
          </TableBody>
        </Table>

        {/* Table footer with count */}
        {sortedKeys.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/30 dark:bg-slate-900/30">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {sortedKeys.length} API {sortedKeys.length === 1 ? "key" : "keys"}{" "}
              total
            </p>
          </div>
        )}
      </div>
    );
  },
);
